import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RiFacebookFill } from 'react-icons/ri';
import { AiFillInstagram } from 'react-icons/ai';
import { FaLinkedinIn, FaBloggerB } from 'react-icons/fa';
import FooterMenu from './FooterMenu';
import { openInNewTab } from '../../utils/helpers/helpers';
import { cn } from '@/utils/cn';
import ArrowRightIcon from '@/assets/svgs/arrow-right-icon';
import { Button } from '../ui/button';

const Footer = ({ className }: { className?: string }) => {
  const location = useLocation();
  const isProPage = location.pathname.includes('/pro');
  const navigate = useNavigate();

  return (
    <footer
      className={cn(
        isProPage ? 'bg-blue-800 py-16' : 'bg-slate-50 py-16',
        className
      )}
    >
      <div className='container mx-auto'>
        <div className='mb-7 flex flex-col gap-y-5 lg:flex-row lg:justify-between lg:gap-x-16'>
          {/* Left Section - Branding and CTA */}
          <div className='flex-1 lg:max-w-md'>
            {/* Logo and Brand */}
            <Link to='/home' title='HireMySub.com' className='flex-none pr-5'>
              <span className='sr-only'>HireMySub</span>
              <img
                src={
                  isProPage
                    ? 'https://cdn.hiremysub.com/hmsLogoWhite.png'
                    : 'https://cdn.hiremysub.com/hmsDefaultLogo.png'
                }
                className='w-44'
                alt='HireMySub Logo'
              />
            </Link>

            {/* Tagline */}
            <p
              className={cn(
                'mb-5 text-lg leading-relaxed font-medium',
                isProPage ? 'text-white' : 'text-slate-500'
              )}
            >
              The fastest way for property professionals and homeowners to find,
              compare, and hire trusted subcontractors.
            </p>

            {/* Call-to-Action Buttons */}
            <div className='tablet:flex-row flex flex-col gap-4'>
              <Button
                variant='accent'
                size='base'
                className='tablet:w-48 w-full'
                onClick={() => navigate('/join')}
                title='Sign Up Today'
                aria-label='Sign Up Today (navigates to sign up page)'
              >
                Sign Up Today
              </Button>
              <Button
                variant='inverse'
                size='base'
                className='tablet:w-48 w-full'
                onClick={() => navigate('/join')}
                title='Join as a Sub'
                aria-label='Join as a Sub (navigates to join page)'
              >
                Join as a Sub
                <ArrowRightIcon size={4} />
              </Button>
            </div>
          </div>

          {/* Right Section - Navigation and Social */}
          <div className='flex-1 lg:flex-none'>
            {/* Navigation Links */}
            <div className='mb-8 flex flex-col lg:flex-row lg:gap-12'>
              <FooterMenu title='For Customers' isProPage={isProPage}>
                <Link
                  to='/'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  How it Works
                </Link>
                <Link
                  to='/customers/search'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Search
                </Link>
                <Link
                  to='/trades'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  All Trades
                </Link>
                <Link
                  to='/join'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Sign Up Free
                </Link>
              </FooterMenu>

              <FooterMenu title='For Subcontractors' isProPage={isProPage}>
                <Link
                  to='/join'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Join as a Sub
                </Link>
                <Link
                  to='/pro/home'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  How it Works (Sub)
                </Link>
                <Link
                  to='/pro/pricing'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Pricing
                </Link>
                <Link
                  to='/pro/project-boards'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Project Board
                </Link>
              </FooterMenu>

              <FooterMenu title='Support' isProPage={isProPage}>
                {/* <Link to='/help-center' className={cn(isProPage ? 'text-white hover:text-primary-blue' : 'hover:text-blue-600')}>
                   Help Center
                 </Link> */}
                <Link
                  to='/contact-us'
                  className={cn(
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                >
                  Contact Us
                </Link>
                {/* <Link to='/faqs' className={cn(isProPage ? 'text-white hover:text-primary-blue' : 'hover:text-blue-600')}>
                   FAQs
                 </Link> */}
                <button
                  title='Check System Status'
                  type='button'
                  role='link'
                  aria-label='Check system status (opens in a new tab)'
                  className={cn(
                    'cursor-pointer text-left',
                    isProPage
                      ? 'hover:text-primary-blue text-white'
                      : 'hover:text-blue-600'
                  )}
                  onClick={() =>
                    openInNewTab('https://stats.uptimerobot.com/pn395f9xBY')
                  }
                >
                  System Status
                </button>
              </FooterMenu>
            </div>

            {/* Social Media Icons */}
            <div className='flex justify-center gap-x-4 lg:justify-end'>
              <button
                title='Blogger'
                type='button'
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border',
                  isProPage
                    ? 'hover:text-primary-blue border-white text-white hover:border-blue-400'
                    : 'border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600'
                )}
              >
                <FaBloggerB
                  size={16}
                  onClick={() =>
                    openInNewTab('https://hiremysub.blogspot.com/')
                  }
                />
              </button>
              <button
                title='Facebook'
                type='button'
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border',
                  isProPage
                    ? 'hover:text-primary-blue border-white text-white hover:border-blue-400'
                    : 'border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600'
                )}
              >
                <RiFacebookFill
                  size={16}
                  onClick={() =>
                    openInNewTab(
                      'https://www.facebook.com/Hiremysubcom-101854902473205'
                    )
                  }
                />
              </button>
              <button
                title='Instagram'
                type='button'
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border',
                  isProPage
                    ? 'hover:text-primary-blue border-white text-white hover:border-blue-400'
                    : 'border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600'
                )}
              >
                <AiFillInstagram
                  size={16}
                  onClick={() =>
                    openInNewTab('https://www.instagram.com/hiremysub/')
                  }
                />
              </button>
              <button
                title='LinkedIn'
                type='button'
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border',
                  isProPage
                    ? 'hover:text-primary-blue border-white text-white hover:border-blue-400'
                    : 'border-gray-300 text-gray-600 hover:border-blue-600 hover:text-blue-600'
                )}
              >
                <FaLinkedinIn
                  size={14}
                  onClick={() =>
                    openInNewTab(
                      'https://www.linkedin.com/company/hiremysub/mycompany/'
                    )
                  }
                />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright and Legal */}
        <hr
          className={cn('mb-6', isProPage ? 'border-white' : 'border-gray-200')}
        />

        <div
          className={cn(
            'flex flex-col items-center gap-y-2 text-sm sm:flex-row sm:justify-between',
            isProPage ? 'text-white' : 'text-gray-500'
          )}
        >
          <p className={isProPage ? 'text-white' : 'text-gray-500'}>
            © HireMySub.com {new Date().getFullYear()}—All Rights Reserved
          </p>
          <div className='flex gap-4'>
            <Link
              to='/privacy'
              className={cn(
                isProPage
                  ? 'hover:text-primary-blue text-white'
                  : 'text-gray-500 hover:text-blue-600'
              )}
            >
              Privacy Policy
            </Link>
            <Link
              to='/terms'
              className={cn(
                isProPage
                  ? 'hover:text-primary-blue text-white'
                  : 'text-gray-500 hover:text-blue-600'
              )}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
